import { Button } from "@gigpro/ui/Button";
import { Checkbox } from "@gigpro/ui/Checkbox";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form, FormControl, FormItem, FormLabel, type SubmitHandler } from "@gigpro/ui/Form";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { Plus } from "@gigpro/ui/Icons";
import { ToggleGroup, ToggleGroupItemUnstyled } from "@gigpro/ui/ToggleGroup";
import { ApiError } from "@sidegigllc/api";
import { getRequirementForWorksListQueryKey, useRequirementForWorksCreate } from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { type Requirement, requirementSchema } from "api/resources/skills";
import React from "react";
import * as z from "zod/mini";

type RequirementItem = { id: number; name: string; category: string };

interface RequirementGroupsProps {
  requirements: Array<RequirementItem>;
  selectedIds: Array<number>;
  onValueChange: (ids: Array<string>) => void;
  skillId: number | null | undefined;
  skillName: string | undefined;
  onRequirementCreated: (requirement: Requirement) => void;
  /** Category the empty-list "Add custom" files into — must match the caller's category filter. */
  emptyCategory?: string;
}

export function RequirementGroups({
  requirements,
  selectedIds,
  onValueChange,
  skillId,
  skillName,
  onRequirementCreated,
  emptyCategory = defaultCategory,
}: Readonly<RequirementGroupsProps>) {
  const [addCustomCategory, setAddCustomCategory] = React.useState<false | string>(false);

  const categories = requirements.reduce<Array<{ category: string; items: Array<RequirementItem> }>>(
    (acc, requirement) => {
      const group = acc.find(({ category }) => category === requirement.category);
      if (group) group.items.push(requirement);
      else acc.push({ category: requirement.category, items: [requirement] });
      return acc;
    },
    [],
  );

  return (
    <>
      <ToggleGroup
        type="multiple"
        value={selectedIds.map(String)}
        onValueChange={onValueChange}
        className="flex flex-col gap-1 bg-transparent"
      >
        {categories.map(({ category, items }) => (
          <React.Fragment key={category}>
            <h3 className="mt-2 text-large font-semibold first:mt-0">{category}</h3>
            <div className="grid max-h-48 gap-2 overflow-auto rounded-sm bg-background p-3 sm:grid-cols-2">
              {items.map(({ id, name }) => (
                <RequirementCheckbox key={id} id={id} name={name} checked={selectedIds.includes(id)} />
              ))}
              {skillId != null && <AddCustom onClick={() => setAddCustomCategory(category)} />}
            </div>
          </React.Fragment>
        ))}
      </ToggleGroup>
      {/* "Add custom" lives inside a category group, so no groups would leave no way to add one. */}
      {categories.length === 0 && skillId != null && (
        <div className="rounded-sm bg-background p-3">
          <p className="text-normal text-gray">No requirements for {skillName ?? "this position"} yet.</p>
          <AddCustom onClick={() => setAddCustomCategory(emptyCategory)} />
        </div>
      )}
      {skillId != null && (
        <NewRequirementDialog
          open={!!addCustomCategory}
          onOpenChange={(open) => setAddCustomCategory((prev) => (open ? prev : false))}
          onSuccess={onRequirementCreated}
          skillId={skillId}
          skillName={skillName}
          category={typeof addCustomCategory === "string" ? addCustomCategory : undefined}
        />
      )}
    </>
  );
}

function RequirementCheckbox({ id, name, checked }: Readonly<{ id: number; name: string; checked: boolean }>) {
  return (
    <FormItem className="flex w-full gap-2 space-y-0">
      <FormControl>
        <ToggleGroupItemUnstyled
          asChild
          value={`${id}`}
          className="data-[state=on]:border-brand data-[state=on]:bg-brand"
        >
          <Checkbox checked={checked} />
        </ToggleGroupItemUnstyled>
      </FormControl>
      <FormLabel>{name}</FormLabel>
    </FormItem>
  );
}

function AddCustom({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <Button type="button" size="small" className="col-span-2 mt-2 justify-start p-0" onClick={onClick}>
      <Plus className="mr-2 size-4" /> Add custom
    </Button>
  );
}

interface NewRequirementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (requirement: Requirement) => void;
  skillId: number;
  skillName: string | undefined;
  category?: string;
}

const defaultCategory = "Other";
const newRequirementFormId = "new-requirement-form";
const NewRequirement = z.omit(requirementSchema, { id: true });
type NewRequirement = z.infer<typeof NewRequirement>;

function NewRequirementDialog({
  open,
  onOpenChange,
  onSuccess,
  skillId,
  skillName,
  category = defaultCategory,
}: Readonly<NewRequirementDialogProps>) {
  const queryClient = useQueryClient();
  const createRequirement = useRequirementForWorksCreate();

  const form = useZodForm({ schema: NewRequirement, values: { name: "", skill: skillId, category } });
  React.useEffect(() => {
    if (open) form.reset({ name: "", skill: skillId, category });
  }, [form, open, skillId, category]);

  const onSubmit: SubmitHandler<NewRequirement> = async (values, event) => {
    event?.stopPropagation();
    try {
      const newRequirement = await createRequirement.mutateAsync({ data: values });
      // Invalidate every list variant (filtered by category and unfiltered) so the new requirement
      // appears whether the caller shows the full list or the Accessories-scoped one.
      await queryClient.invalidateQueries({ queryKey: getRequirementForWorksListQueryKey() });
      onSuccess(newRequirement);
      onOpenChange(false);
    } catch (error) {
      ApiError.fromThrown(error).setFormErrors(form.setError);
    }
  };

  const categoryTitle = category === defaultCategory ? "Requirement" : category;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-md">
      <DialogTitle>
        Add {categoryTitle} for {skillName}
      </DialogTitle>
      <Form id={newRequirementFormId} form={form} onSubmit={onSubmit}>
        <Textfield
          control={form.control}
          name="name"
          label="Name"
          placeholder={`Enter ${categoryTitle.toLowerCase()} name...`}
          autoComplete="off"
          maxLength={40}
        />
      </Form>
      <DialogFooter>
        <Button size="small" onClick={() => onOpenChange(false)} disabled={form.formState.isSubmitting}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          type="submit"
          loading={form.formState.isSubmitting}
          form={newRequirementFormId}
          onClick={(e) => {
            // if not prevented it leads to a weird behavior where the outer form dialog closes itself
            e.preventDefault();
            void form.handleSubmit(onSubmit)(e);
          }}
        >
          Add
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
